import React, { useEffect, useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import NumberFormat from "react-number-format";
import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import PageviewIcon from "@mui/icons-material/Pageview";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import ButtonGroup from "@mui/material/ButtonGroup";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";

import { projectStatusOptions } from "utils/data";
import SuiBadge from "components/SuiBadge";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";
import SuiBox from "components/SuiBox";
import SuiAvatar from "components/SuiAvatar";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import { Carousel } from "antd";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";
import qs from "query-string";
import { useForm, Controller } from "react-hook-form";

import defaultStyleImage from "assets/images/upstyled/default_background.png";

import { filterParser, sortParser } from "utils/helper";
import { projectRequestsSortOptions, MANUFACTURER_SERVICES } from "utils/data";
import { getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import ProjectDetail from "./detail";

const AdminProjects = () => {
  const [data, setData] = useState({ hits: [], nbHits: 0, estimatedTotalHits: 0 });
  const [detail, setDetail] = useState({});
  const user = useContext(AccountContext);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchStr, setSearchStr] = useState("");
  const [filterObj, setFilterObj] = useState({});

  let location = useLocation();
  const url = location.href ? location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url);
  const projectId = urlObj?.query?.id;

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    reset({ sort_by: projectRequestsSortOptions[0] });
  }, []);

  useEffect(() => {
    if (user.id) {
      const offset = pageSize * (page - 1);

      retrieveProjects(offset, pageSize, "", {});
    }
  }, [user.id]);

  useEffect(() => {
    if (projectId) {
      retrieveProjectDetail(projectId);
    }
  }, [projectId]);

  const retrieveProjects = async (offset = 0, limit = pageSize, q = "", filter = {}) => {
    const payload = { offset, limit };
    if (q) {
      payload["q"] = q;
    }
    let myFilter = {};
    if (Object.keys(filter).length > 0) {
      myFilter = { ...myFilter, ...filter };
    }

    //get sorting options from form values
    const sortOpt = getValues("sort_by");
    let sortStr = "";
    if (sortOpt && sortOpt.value) {
      sortStr = sortParser(sortOpt.value);
    }

    const newFilterStr = filterParser(myFilter);
    if (newFilterStr) {
      payload["filter"] = newFilterStr;
      setFilterObj(myFilter);
    }
    if (sortStr) {
      payload["sort"] = [sortStr];
    }

    try {
      //const { data } = await api.project.list({ owner_id: user.id });

      const { data } = await api.project.search_admin(payload);
      setData(data ? data : { hits: [], nbHits: 0, estimatedTotalHits: 0 });
    } catch (err) {
      console.log(err);
    }
  };

  const retrieveProjectDetail = async (id) => {
    try {
      const { data } = await api.project.get(id);
      const project = data["project"] ? data["project"] : {};
      setDetail(project);
    } catch (err) {
      toast.error("Get project detail failed!");
      console.log(err);
    }
  };

  const handleApply = (data) => {
    const offset = pageSize * (page - 1);
    const limit = pageSize;
    setSearchStr(data["keywords"]);
    let myFilter = {};

    //status filter

    if (data["status"] && data["status"].value) {
      myFilter["status"] = data["status"].value;
    }

    retrieveProjects(offset, limit, data["keywords"], myFilter);
  };

  const formatAccountStatus = (type, row) => {
    const option = projectStatusOptions.find((x) => x.value == type);
    if (option) {
      if (option.value == "init") {
        return <SuiBadgeDot color="warning" badgeContent={option.label} />;
      } else if (option.value == "requested") {
        return <SuiBadgeDot size="xs" color="info" badgeContent={option.label} />;
      } else if (option.value == "active") {
        return <SuiBadgeDot size="xs" color="success" badgeContent={option.label} />;
      } else if (option.value == "canceled") {
        return <SuiBadgeDot color="error" badgeContent={option.label} />;
      } else if (option.value == "completed") {
        return <SuiBadgeDot color="dark" badgeContent={option.label} />;
      } else if (row?.original?.deleted_at) {
        return (
          <Tooltip
            title={`Deleted at ${moment(row.original.deleted_at).format("YYYY-MM-DD")}`}
            placement="top"
          >
            <SuiBadgeDot color="error" badgeContent={"Deleted"} />
          </Tooltip>
        );
      } else {
        return <SuiBadgeDot color="secondary" badgeContent={option.label} />;
      }
    } else {
      return type;
    }
  };

  const getProjectTableRow = (item) => {
    const actions = [
      {
        label: (
          <Link to={`/a/projects?id=${item.id}`} style={{ color: "inherit" }}>
            View
          </Link>
        ),
        icon: <PageviewIcon fontSize={"small"} />,
      },
    ];
    const products = item.products;
    let styleImages = [];
    for (let i = 0; i < products.length; i++) {
      const images = getStyleImages(products[i], true);
      if (images && images.length > 0) {
        styleImages.push(images[0]);
      }
    }
    if (styleImages.length == 0) {
      styleImages.push({ url: defaultStyleImage });
    }

    const responseMap = item.response || {};
    const responsedManufacturers = Object.keys(responseMap);

    return {
      key: item.id,
      name: item.name,
      images: styleImages,
      created_at: item.created_at,
      updated_at: item.updated_at,
      deleted_at: item.deleted_at,
      status: item.status,
      owner_name: item.store_name,
      response_number: responsedManufacturers.length,
      action: <Actionables actions={actions} />,
    };
  };

  const columns = [
    {
      Header: "Image",
      accessor: "images",
      Cell: ({ row, value }) => {
        return (
          <Link to={`/a/projects?id=${row?.original?.key}`} style={{ color: "inherit" }}>
            <SuiBox
              sx={{
                width: 60,
                height: 60,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "transparent",
                boxShadow: "none",
                overflow: "visible",
              }}
            >
              <Carousel arrows={true} dots={false}>
                {value.map((img) => (
                  <SuiBox
                    src={
                      img.url
                        ? canResizeImageUrl(img.type)
                          ? img.url + "?x-oss-process=image/resize,m_fill,h_60"
                          : img.url
                        : img.preview
                    }
                    component="img"
                    sx={{
                      overflow: "hidden",
                      width: "100%",

                      height: 60,
                      margin: 0,
                      objectFit: "contain",
                      objectPosition: "center",
                    }}
                  />
                ))}
              </Carousel>
            </SuiBox>
          </Link>
        );
      },
    },
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row, value }) => {
        return <div className="max-w-[200px]">{value}</div>;
      },
    },
    {
      Header: "Replied Manufacturers",
      accessor: "response_number",
      Cell: ({ row, value }) => {
        return <div className="max-w-[200px]">{value}</div>;
      },
    },
    {
      Header: "Updated At",
      accessor: "updated_at",
      Cell: ({ row, value }) => {
        return <div>{moment(value).format("YYYY-MM-DD  HH:mm")}</div>;
      },
    },
    {
      Header: "Created At",
      accessor: "created_at",
      Cell: ({ row, value }) => {
        return <div>{moment(value).format("YYYY-MM-DD HH:mm")}</div>;
      },
    },
    {
      Header: "status",
      accessor: "status",
      Cell: ({ row, value }) => formatAccountStatus(value, row),
    },
    {
      Header: "Owner",
      accessor: "owner_name",
    },
    { Header: "More", accessor: "action" },
  ];

  const rows = data.hits.map((item) => getProjectTableRow(item));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const offset = pageSize * (newPage - 1);
    const limit = pageSize;
    retrieveProjects(offset, limit, searchStr, filterObj);
  };

  const handleChangeRowsPerPage = (option) => {
    const newPageSize = parseInt(option.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    const offset = 0;
    const limit = newPageSize;
    retrieveProjects(offset, limit, searchStr, filterObj);
  };
  const totalNumber = data.nbHits ? data.nbHits : data.estimatedTotalHits;
  const pageCount = parseInt(Math.ceil(totalNumber / pageSize), 10);
  const pageOptions = [];
  for (let i = 0; i < pageCount; i++) {
    pageOptions.push(i);
  }
  const entriesStart = pageSize * (page - 1) + 1;
  const entriesEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;

  if (projectId) {
    return (
      <ProjectDetail project={detail} handleRefresh={() => retrieveProjectDetail(projectId)} />
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <SuiBox display="flex" justifyContent="space-between" alignItems="flex-end">
        <SuiBox component="form" pb={3} onSubmit={handleSubmit(handleApply)}>
          <SuiBox
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <SuiBox display="flex" alignItems="center" sx={{ gap: 1.5, flexWrap: "wrap" }}>
              <SuiTypography variant="button" color="dark" fontWeight="regular">
                Filter projects by:
              </SuiTypography>
              <div style={{ width: 160 }}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <SuiSelect
                      {...field}
                      options={projectStatusOptions}
                      placeholder="status"
                      backspaceRemovesValue
                      isClearable
                    />
                  )}
                />
              </div>

              <div style={{ width: 160 }}>
                <Controller
                  name="sort_by"
                  control={control}
                  render={({ field }) => (
                    <SuiSelect
                      {...field}
                      options={projectRequestsSortOptions}
                      placeholder="Sort By"
                      backspaceRemovesValue
                    />
                  )}
                />
              </div>
              <SuiBox>
                <SuiInput placeholder="Keywords" type="text" {...register("keywords")} />
              </SuiBox>
              <div>
                <SuiButton variant="gradient" color="dark" type="submit">
                  Apply
                </SuiButton>
              </div>
            </SuiBox>
          </SuiBox>
        </SuiBox>
      </SuiBox>
      <DataTable
        table={{ columns, rows: rows }}
        entriesPerPage={{ defaultValue: pageSize }}
        showTotalEntries={false}
        pagination={false}
        noEndBorder
      />

      <SuiBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={3}
      >
        <div className="flex items-center">
          <SuiBox mb={{ xs: 3, sm: 0 }}>
            <SuiTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {totalNumber} entries
            </SuiTypography>
          </SuiBox>

          <SuiBox display="flex" alignItems="center" gap={1}>
            <SuiSelect
              defaultValue={{ value: pageSize, label: pageSize }}
              options={[10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
              onChange={handleChangeRowsPerPage}
              size="small"
            />
            <SuiTypography variant="caption" color="secondary">
              entries per page
            </SuiTypography>
          </SuiBox>
        </div>
        <Pagination
          variant={"outlined"}
          color={"info"}
          count={pageOptions.length}
          defaultPage={page}
          onChange={handleChangePage}
        />
      </SuiBox>
    </Card>
  );
};

export default AdminProjects;
